"use client";

import { use, useId, useMemo } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { isEmailSuggestionMessage } from "@shared/formats/email";
import {
  FieldControlContext,
  useFieldControlContextValue,
  useFieldMessage,
} from "@/components/ui/field-control";

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "flex flex-col gap-4",
        "has-[>[data-slot=checkbox-group]]:gap-2 has-[>[data-slot=radio-group]]:gap-2",
        className,
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        "mb-2 font-medium",
        "data-[variant=legend]:text-sm",
        "data-[variant=label]:text-xs tracking-[0.1em]",
        className,
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "group/field-group @container/field-group flex w-full flex-col gap-3 data-[slot=checkbox-group]:gap-2 [&>[data-slot=field-group]]:gap-3",
        className,
      )}
      {...props}
    />
  );
}

function FieldGroupItem({
  className,
  label,
  required,
  optional,
  htmlFor,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  label: React.ReactNode;
  required?: boolean;
  optional?: boolean;
  htmlFor?: string;
}) {
  return (
    <FieldGroup className={className} {...props}>
      <FieldLabel htmlFor={htmlFor} required={required} optional={optional}>
        {label}
      </FieldLabel>
      {children}
    </FieldGroup>
  );
}

const fieldVariants = cva(
  "group/field data-[invalid=true]:[&>[data-slot=field-label]]:text-destructive-strong flex w-full gap-1.5",
  {
    variants: {
      orientation: {
        vertical: ["flex-col [&>*]:w-full [&>.sr-only]:w-auto"],
        horizontal: [
          "flex-row items-center",
          "[&>[data-slot=field-label]]:flex-auto",
          "has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px has-[>[data-slot=field-content]]:items-start",
        ],
        responsive: [
          "@md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto flex-col [&>*]:w-full [&>.sr-only]:w-auto",
          "@md/field-group:[&>[data-slot=field-label]]:flex-auto",
          "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  },
);

function Field({
  className,
  orientation = "vertical",
  controlId,
  "data-invalid": dataInvalid,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof fieldVariants> & {
    controlId?: string;
    "data-invalid"?: boolean;
  }) {
  const field = useFieldControlContextValue(controlId);
  return (
    <FieldControlContext value={field}>
      <div
        role="group"
        data-slot="field"
        data-orientation={orientation}
        data-invalid={dataInvalid ?? (field.invalid || undefined)}
        className={cn(fieldVariants({ orientation }), className)}
        {...props}
      />
    </FieldControlContext>
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "group/field-content flex flex-1 flex-col gap-1.5 leading-snug",
        className,
      )}
      {...props}
    />
  );
}

function FieldLabel({
  required,
  optional,
  className,
  htmlFor,
  ...props
}: React.ComponentProps<typeof Label> & {
  required?: boolean;
  optional?: boolean;
}) {
  const field = use(FieldControlContext);
  return (
    <Label
      data-slot="field-label"
      htmlFor={htmlFor ?? field?.controlId}
      className={cn(
        "group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50",
        "text-xs font-medium text-muted-foreground text-muted-foreground",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-sm has-[>[data-slot=field]]:border [&>[data-slot=field]]:p-3",
        "has-data-[state=checked]:bg-action-tint has-data-[state=checked]:border-primary",
        className,
      )}
      {...props}
    >
      {props.children}
      {required && <span className="text-destructive-strong ml-0.5">*</span>}
      {optional && (
        <span className="text-muted-foreground ml-1 text-[0.8125em] font-normal tracking-normal">
          (optional)
        </span>
      )}
    </Label>
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "flex w-fit items-center gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function FieldDescription({
  className,
  id,
  ...props
}: React.ComponentProps<"p">) {
  const generatedId = useId();
  const descriptionId = id ?? generatedId;
  useFieldMessage(descriptionId, "description", true);
  return (
    <p
      id={descriptionId}
      data-slot="field-description"
      className={cn(
        "text-muted-foreground text-xs font-normal leading-normal group-has-[[data-orientation=horizontal]]/field:text-balance",
        "nth-last-2:-mt-0.5 last:mt-0 [[data-variant=legend]+&]:-mt-1",
        "[&>a:hover]:text-action-strong [&>a]:underline [&>a]:underline-offset-4",
        className,
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode;
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        "relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
        className,
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

function isNonEmptyMessage(
  message: string | null | undefined,
): message is string {
  return typeof message === "string" && message.length > 0;
}

type FieldErrorEntry = string | { message?: string } | undefined;

function fieldErrorContent(
  children: React.ReactNode,
  errors: Array<FieldErrorEntry> | undefined,
  suggesting: boolean,
): React.ReactNode {
  const shown = (message: string) => !suggesting || !isEmailSuggestionMessage(message);
  if (children) {
    return typeof children === "string" && !shown(children) ? null : children;
  }

  if (!errors || errors.length === 0) {
    return null;
  }

  const messages = errors
    .map((error) => {
      if (typeof error === "string") return error;
      if (error && typeof error === "object" && "message" in error)
        return error.message;
      return null;
    })
    .filter(isNonEmptyMessage)
    .filter(shown);

  if (messages.length === 0) {
    return null;
  }

  if (messages.length === 1) {
    return messages[0];
  }

  return (
    <ul className="ml-3 flex list-disc flex-col gap-0.5">
      {messages.map((message, index) => (
        <li key={index}>{message}</li>
      ))}
    </ul>
  );
}

function FieldError({
  className,
  children,
  errors,
  id,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<string | { message?: string } | undefined>;
}) {
  const suggesting = use(FieldControlContext)?.suggesting ?? false;
  const content = useMemo(
    () => fieldErrorContent(children, errors, suggesting),
    [children, errors, suggesting],
  );
  const generatedId = useId();
  const errorId = id ?? generatedId;
  useFieldMessage(errorId, "error", Boolean(content));

  if (!content) {
    return null;
  }

  return (
    <div
      id={errorId}
      role="alert"
      data-slot="field-error"
      className={cn("text-destructive-strong text-xs font-normal", className)}
      {...props}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldGroupItem,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
};
